import { AddCircle } from '@mui/icons-material';
import { Box, Button, Grid, Paper } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import MainContentHeader from 'src/shared/components/atoms/mainContentHeader';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { useLocalStorage } from 'src/shared/hooks/useLocalStorage';
import { LocalStorageKeys } from 'src/shared/models/enums/localStorageKeys.enum';
import { PageFilterSection } from 'src/shared/components/organisms/pageFilterSection';
import SearchField from 'src/shared/components/form/searchField';
import PageTabNavigation, { PageTabItem } from 'src/shared/components/navigations/pageTabNavigation';
import { RoutesEnum } from 'src/shared/models/enums/routes.enum';
import { DEFAULT_PAGE_INDEX, DEFAULT_PAGE_SIZE } from 'src/shared/models/enums/gridFilterConfig';
import { InvoiceArticleCategoryDataGrid } from '../components/invoiceArticleCategoryDataGrid';
import { ArticleCategorySearchFilter } from '../models/articleCategorySearchFilter';
import { CreateArticleCategory } from '../components/createArticleCategory';
import { EditArticleCategory } from '../components/editArticleCategory';

export const ArticleCategoryPage = () => {
  const { t } = useTranslation();
  const isMounted = useIsMounted();

  const [openEditCategory, setOpenEditCategory] = useState<boolean>(false);
  const [articleCategoryId, setArticleCategoryId] = useState<number | undefined>(undefined);
  const [openCreateCategory, setOpenCreateCategory] = useState<boolean>(false);
  const [refetchDataGrid, setRefetchDataGrid] = useState<boolean>(false);
  const [tempSearchString, setTempSearchString] = useState<string | undefined>(undefined);
  const [searchInitialized, setSearchInitialized] = useState<boolean>(false);

  const [articleCategoryFilter, setArticleCategoryFilter] = useLocalStorage(LocalStorageKeys.ArticleCategoryFilter, {
    sortCol: 'articleCategory',
    sortDir: 'desc',
    pageIndex: DEFAULT_PAGE_INDEX,
    pageSize: DEFAULT_PAGE_SIZE,
  } as ArticleCategorySearchFilter);

  const searchFilter: ArticleCategorySearchFilter = useMemo(() => {
    return {
      filter: articleCategoryFilter?.filter,
      pageSize: articleCategoryFilter?.pageSize,
      pageIndex: articleCategoryFilter?.pageIndex,
      sortCol: articleCategoryFilter?.sortCol,
      sortDir: articleCategoryFilter?.sortDir,
    } as ArticleCategorySearchFilter;
  }, [
    articleCategoryFilter?.filter,
    articleCategoryFilter?.pageIndex,
    articleCategoryFilter?.pageSize,
    articleCategoryFilter?.sortCol,
    articleCategoryFilter?.sortDir,
  ]);

  useEffect(() => {
    if (isMounted() && searchInitialized) {
      if (tempSearchString === '') {
        setArticleCategoryFilter((prev) => ({ ...prev, ...{ filter: '', pageIndex: 0 } }));
      }
      if (tempSearchString && tempSearchString.length >= 2) {
        setArticleCategoryFilter((prev) => ({ ...prev, ...{ filter: tempSearchString, pageIndex: 0 } }));
      } else {
        setArticleCategoryFilter((prev) => ({ ...prev, ...{ filter: '', pageIndex: 0 } }));
      }
    }
  }, [isMounted, searchInitialized, setArticleCategoryFilter, tempSearchString]);

  const pageTabs: PageTabItem[] = [
    {
      label: t('invoice:articleRegisterTabs.articles'),
      route: RoutesEnum.InvoiceArticleRegister,
    },
    {
      label: t('invoice:articleRegisterTabs.category'),
      route: RoutesEnum.InvoiceArticleCategory,
    },
  ];

  return (
    <GridWrapper>
      <Grid
        item
        xs={6}
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        <MainContentHeader title={t('invoice:articleCategoryPage.header')} />
      </Grid>

      <Grid item xs={6} style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          onClick={() => setOpenCreateCategory(true)}
          sx={{ marginRight: 2 }}
          variant='contained'
          color='secondary'
          startIcon={<AddCircle fontSize='small' />}
          disableElevation
        >
          {t('invoice:articleCategoryPage.createCategory')}
        </Button>
      </Grid>

      <Grid item xs={12}>
        <Paper>
          <PageTabNavigation pageTabs={pageTabs} />
          <PageFilterSection
            leftContent={
              <SearchField
                initialValue={searchFilter.filter || ''}
                onSearch={(newSearchString) => {
                  setSearchInitialized(true);
                  setTempSearchString(newSearchString);
                }}
              />
            }
          />
          <Box className='section-inset'>
            <InvoiceArticleCategoryDataGrid
              refetchDataGrid={refetchDataGrid}
              setRefetchDataGrid={setRefetchDataGrid}
              setArticleCategoryId={setArticleCategoryId}
              setOpenEditCategory={setOpenEditCategory}
              searchFilter={searchFilter}
              searchFilterCallback={(newFilter) =>
                setArticleCategoryFilter((prev) => ({
                  ...prev,
                  ...newFilter,
                }))
              }
            />
          </Box>
        </Paper>
      </Grid>
      {openCreateCategory && <CreateArticleCategory setOpenCreateArticle={setOpenCreateCategory} setRefetchDataGrid={setRefetchDataGrid} />}
      {openEditCategory && articleCategoryId && (
        <EditArticleCategory
          key={articleCategoryId}
          articleCategoryId={articleCategoryId}
          setArticleCategoryId={setArticleCategoryId}
          setOpenEditCategory={setOpenEditCategory}
          setRefetchDataGrid={setRefetchDataGrid}
        />
      )}
    </GridWrapper>
  );
};
